import React, { forwardRef, HTMLAttributes, ReactElement, useEffect, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Text, Col, Row, ContentPlaceholder } from '@evertel/web/ui'
import { UserController, useUserTagline } from '@evertel/blue-user'
import { ProfilePic } from './ProfilePic'
import { useService } from '@evertel/di'
import { DepartmentsAccessController } from '@evertel/departments-access'

// ********************************************************************
// NOTE: this component should only be used where you are expecting
// the user to be stored in the usersStore. If it can't find the user
// it will fetch the user. DO NOT use in user's search where results
// are not intended to be stored. Instead use the UserTileRawData component
// ********************************************************************

interface UserTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
    userId: number
    imageWidth?: number
    showPosition?: boolean
    subComponent?: ReactElement
    rightComponent?: ReactElement
    className?: string
    onClick?: () => void
}

const UserTile = observer(forwardRef<HTMLDivElement, UserTileProps>(({
    userId,
    imageWidth = 30,
    showPosition,
    subComponent,
    rightComponent,
    className,
    ...otherProps
}, ref) => {

    const userController = useService(UserController, [])
    const departmentsAccessController = useService(DepartmentsAccessController, [])

    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        setIsLoading(true)
        userController.init(userId)
        if (showPosition) {
            departmentsAccessController.init(userId)
        }
        setIsLoading(false)
    }, [userId])

    const { displayPosition } = useUserTagline({
        departmentsAccess: departmentsAccessController.departmentsAccess,
        contextDepartmentId: undefined,
        reportingDepartment: userController.user?.reportingDepartment
    })

    const _className = classNames(
        'user-tile',
        'p-0',
        'd-flex',
        {
            'cursor-pointer': otherProps?.onClick
        },
        className
    )

    if (isLoading) {
        return (
            <div className="d-flex">
                <ContentPlaceholder width={imageWidth} height={imageWidth} className="me-2" />
                <ContentPlaceholder width="40%" />
            </div>
        )
    }

    return (
        <Row
            ref={ref}
            className={_className}
            {...otherProps}>
            <ProfilePic
                userId={userId}
                width={imageWidth}
                height={imageWidth}
            />
            <Col
                xs={(rightComponent) ? 7 : 11}
                className="p-0 ms-2">
                <Text bold className="text-truncate" >
                    {userController.fullName ?? userController.user?.email ?? userController.userId}
                </Text>
                {showPosition && (
                    <Text size="smaller">
                        {displayPosition}
                    </Text>
                )}
                {subComponent && React.cloneElement(subComponent)}
            </Col>
            {rightComponent && React.cloneElement(rightComponent)}
        </Row>
    )
}))

UserTile.displayName = 'UserTile'

export { UserTile }
